// eslint-disable-next-line no-restricted-imports
import { useLocation, useMatches } from '@remix-run/react'
import * as Sentry from '@sentry/remix'
import { useEffect } from 'react'
import type { EnvType } from '~/env'
import { IGNORE_PATTERNS } from '~/lib/error/ignorePatterns'
import { maskMessage } from '~/lib/error/mask'
import { ignoreOldBrowser } from '~/lib/error/ua'

// 絶対に一度だけの初期化を保証するため useRef でなくグローバル変数で管理する
let initialized = false

// environment (APP_ENV) を設定したいので entry.client.tsx でなく hydration 後の root.tsx で初期化する
export function useInitSentry(env?: EnvType) {
  useEffect(() => {
    if (initialized) {
      return
    }
    if (env === undefined || env.APP_ENV === undefined) {
      return
    }
    initialized = true
    const suffix = import.meta.env.VITE_MODE === 'ppg' ? '-ppg' : ''
    initSentry(`${env.APP_ENV}${suffix}`)
  }, [env, env?.APP_ENV])
}

function initSentry(environment: string) {
  if (process.env.NODE_ENV === 'development') {
    return
  }
  Sentry.init({
    environment,

    dsn: 'https://f89da4cbeb26303124a1cf3fe391baa4@o422373.ingest.sentry.io/4505825711882240',
    integrations: [
      Sentry.browserTracingIntegration({
        useEffect,
        useLocation,
        useMatches,
      }),
      Sentry.replayIntegration(),
    ],

    // デフォルトでは250文字までしか送信されず、調査で追加情報を出力したい場合に
    // 足りなくなってしまうため4096文字に設定する
    // @see: https://docs.sentry.io/platforms/javascript/guides/remix/configuration/options/#max-value-length
    maxValueLength: 4096,
    // プロジェクトごとの割り当てに従った調整値
    // @see https://ikyu.slack.com/archives/C0F6QL963/p1699422977244319?thread_ts=1699261750.138099&cid=C0F6QL963
    // @see https://docs.google.com/spreadsheets/d/1m51sYdvwyOm8uR3EJ0dX0J9pO5cPqTV9cbvvxDvG74g/edit#gid=881380573
    // トランザクションのサンプリング率
    tracesSampleRate: 0.003,
    // リプレイ機能のサンプリング率
    replaysSessionSampleRate: 0.0001,
    // エラーが発生したユーザーのリプレイ機能のサンプリング率
    replaysOnErrorSampleRate: 0.01,

    beforeSend(event, _hint) {
      // ignore old browsers
      if (typeof window !== 'undefined' && typeof window.navigator !== 'undefined') {
        if (ignoreOldBrowser(window.navigator.userAgent)) {
          return null
        }
      }

      if (event.message) {
        event.message = maskMessage(event.message)
      }
      if (event.request?.query_string) {
        const qp = event.request.query_string
        if (typeof qp === 'string') {
          event.request.query_string = maskMessage(qp)
        } else if (Array.isArray(qp)) {
          event.request.query_string = qp.map(
            ([k, v]) => [k, maskMessage(v)] satisfies [string, string]
          )
        } else {
          for (const k in qp) {
            qp[k] = maskMessage(qp[k])
          }
        }
      }
      if (event.exception && event.exception.values) {
        // 大本の event.exception を書き換えないといけない
        // forEach で exception.values をひとつずつ書き換えても送信データに反映されなかった
        event.exception = {
          values: event.exception.values.map((e) => {
            if (e.value) {
              e.value = maskMessage(e.value)
            }
            return e
          }),
        }
      }
      return event
    },

    beforeSendTransaction(transaction) {
      if (transaction.message) {
        transaction.message = maskMessage(transaction.message)
      }
      return transaction
    },

    // @see: https://docs.sentry.io/platforms/javascript/guides/react/configuration/filtering/#decluttering-sentry
    ignoreErrors: [
      // Random plugins/extensions
      'top.GLOBALS',
      // See: http://blog.errorception.com/2012/03/tale-of-unfindable-js-error.html
      'originalCreateNotification',
      'canvas.contentDocument',
      'MyApp_RemoveAllHighlights',
      'http://tt.epicplay.com',
      "Can't find variable: ZiteReader",
      'jigsaw is not defined',
      'ComboSearch is not defined',
      'http://loading.retry.widdit.com/',
      'atomicFindClose',
      // Facebook borked
      'fb_xd_fragment',
      // ISP "optimizing" proxy - `Cache-Control: no-transform` seems to
      // reduce this. (thanks @acdha)
      // See http://stackoverflow.com/questions/4113268
      'bmi_SafeAddOnload',
      'EBCallBackMessageReceived',
      // See http://toolbar.conduit.com/Developer/HtmlAndGadget/Methods/JSInjection.aspx
      'conduitPage',
      ...IGNORE_PATTERNS,
    ],

    denyUrls: [
      // Facebook flakiness
      /graph\.facebook\.com/i,
      // Facebook blocked
      /connect\.facebook\.net\/en_US\/all\.js/i,
      // Woopra flakiness
      /eatdifferent\.com\.woopra-ns\.com/i,
      /static\.woopra\.com\/js\/woopra\.js/i,
      // Chrome extensions
      /extensions\//i,
      /^chrome:\/\//i,
      /^chrome-extension:\/\//i,
      // Other plugins
      /127\.0\.0\.1:4001\/isrunning/i, // Cacaoweb
      /webappstoolbarba\.texthelp\.com\//i,
      /metrics\.itunes\.apple\.com\.edgesuite\.net\//i,
    ],
  })
}

/** production では captureException し、それ以外では throw する */
export function captureExceptionOnProductionOrThrow(e: unknown) {
  if (import.meta.env.VITE_APP_ENV === 'production') {
    Sentry.captureException(e)
  } else {
    throw e
  }
}
